/**
 * ProactiveSettings - User preferences for proactive AI assistance
 *
 * Allows users to configure:
 * - Proactive mode (off/gentle/active)
 * - Quiet hours
 * - Confirmation preferences for external actions
 */

import { useState, useEffect, useCallback } from "react";
import { Form, Card, Spinner, Alert, Button } from "react-bootstrap";
import { supabase } from "../supabaseClient";
import "./ProactiveSettings.css";

const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || "http://localhost:3000";

const PROACTIVE_MODES = [
  {
    value: "off",
    label: "Off",
    description: "I'll only help when you ask. No proactive suggestions.",
    icon: "bi-toggle-off",
  },
  {
    value: "gentle",
    label: "Gentle",
    description: "I'll suggest helpful actions but never act on my own.",
    icon: "bi-lightbulb",
  },
  {
    value: "active",
    label: "Active",
    description: "I'll take actions with your confirmation each time.",
    icon: "bi-lightning-charge",
  },
];

const QUIET_HOURS_PRESETS = [
  { label: "None", start: null, end: null },
  { label: "Night (10 PM - 7 AM)", start: "22:00", end: "07:00" },
  { label: "Work hours (9 AM - 5 PM)", start: "09:00", end: "17:00" },
  { label: "Custom", start: "custom", end: "custom" },
];

export default function ProactiveSettings({ userId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Settings state
  const [displayName, setDisplayName] = useState("");
  const [proactiveMode, setProactiveMode] = useState("off");
  const [quietHoursStart, setQuietHoursStart] = useState(null);
  const [quietHoursEnd, setQuietHoursEnd] = useState(null);
  const [quietHoursPreset, setQuietHoursPreset] = useState("None");
  const [alwaysConfirmExternal, setAlwaysConfirmExternal] = useState(true);

  // Heartbeat settings
  const [heartbeatEnabled, setHeartbeatEnabled] = useState(true);
  const [heartbeatMode, setHeartbeatMode] = useState("full");
  const [heartbeatActiveStart, setHeartbeatActiveStart] = useState("08:00");
  const [heartbeatActiveEnd, setHeartbeatActiveEnd] = useState("22:00");

  // Load current settings
  useEffect(() => {
    async function loadSettings() {
      if (!userId) return;

      try {
        const response = await fetch(`${API_URL}/api/user/profile?user_id=${userId}`);
        const data = await response.json();

        if (data.success && data.profile) {
          setDisplayName(data.profile.display_name || "");
          setProactiveMode(data.profile.proactive_mode || "off");
          setQuietHoursStart(data.profile.quiet_hours_start);
          setQuietHoursEnd(data.profile.quiet_hours_end);
          setAlwaysConfirmExternal(data.profile.always_confirm_external_actions ?? true);

          // Heartbeat settings
          setHeartbeatEnabled(data.profile.heartbeat_enabled ?? true);
          setHeartbeatMode(data.profile.heartbeat_mode || "full");
          if (data.profile.heartbeat_active_hours) {
            setHeartbeatActiveStart(data.profile.heartbeat_active_hours.start || "08:00");
            setHeartbeatActiveEnd(data.profile.heartbeat_active_hours.end || "22:00");
          }

          // Determine preset from times
          if (!data.profile.quiet_hours_start) {
            setQuietHoursPreset("None");
          } else if (data.profile.quiet_hours_start === "22:00" && data.profile.quiet_hours_end === "07:00") {
            setQuietHoursPreset("Night (10 PM - 7 AM)");
          } else if (data.profile.quiet_hours_start === "09:00" && data.profile.quiet_hours_end === "17:00") {
            setQuietHoursPreset("Work hours (9 AM - 5 PM)");
          } else {
            setQuietHoursPreset("Custom");
          }
        } else if (data.needs_migration) {
          // Fallback to defaults if migration not run
          console.warn("[ProactiveSettings] Migration needed");
        }
      } catch (err) {
        console.error("[ProactiveSettings] Error loading:", err);
        setError("Failed to load settings");
      } finally {
        setLoading(false);
      }
    }

    loadSettings();
  }, [userId]);

  // Save settings
  const saveSettings = useCallback(async () => {
    if (!userId) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_URL}/api/user/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          display_name: displayName || null,
          proactive_mode: proactiveMode,
          quiet_hours_start: quietHoursStart,
          quiet_hours_end: quietHoursEnd,
          always_confirm_external_actions: alwaysConfirmExternal,
          heartbeat_enabled: heartbeatEnabled,
          heartbeat_mode: heartbeatMode,
          heartbeat_active_hours: { start: heartbeatActiveStart, end: heartbeatActiveEnd },
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess("Settings saved successfully");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(data.error || "Failed to save settings");
      }
    } catch (err) {
      console.error("[ProactiveSettings] Error saving:", err);
      setError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }, [userId, displayName, proactiveMode, quietHoursStart, quietHoursEnd, alwaysConfirmExternal, heartbeatEnabled, heartbeatMode, heartbeatActiveStart, heartbeatActiveEnd]);

  // Handle quiet hours preset change
  const handleQuietHoursPresetChange = (preset) => {
    setQuietHoursPreset(preset);
    const presetConfig = QUIET_HOURS_PRESETS.find((p) => p.label === preset);
    if (presetConfig && presetConfig.start !== "custom") {
      setQuietHoursStart(presetConfig.start);
      setQuietHoursEnd(presetConfig.end);
    }
  };

  if (loading) {
    return (
      <div className="proactive-settings-loading">
        <Spinner animation="border" size="sm" />
        <span>Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="proactive-settings">
      <Card className="proactive-settings-card">
        <Card.Header>
          <h5 className="mb-0">
            <i className="bi bi-sliders me-2"></i>
            Preferences
          </h5>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Display Name */}
          <Form.Group className="mb-4">
            <Form.Label className="proactive-label">
              <i className="bi bi-person me-2"></i>
              What should I call you?
            </Form.Label>
            <Form.Control
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your nickname"
              className="proactive-input"
              maxLength={50}
            />
            <Form.Text className="text-muted">
              I'll use this name to personalize our conversations.
            </Form.Text>
          </Form.Group>

          {/* Proactive Mode */}
          <Form.Group className="mb-4">
            <Form.Label className="proactive-label">
              <i className="bi bi-lightning me-2"></i>
              Proactive Assistance
            </Form.Label>
            <div className="proactive-mode-options">
              {PROACTIVE_MODES.map((mode) => (
                <div
                  key={mode.value}
                  className={`proactive-mode-option ${proactiveMode === mode.value ? "active" : ""}`}
                  onClick={() => setProactiveMode(mode.value)}
                >
                  <div className="proactive-mode-icon">
                    <i className={`bi ${mode.icon}`}></i>
                  </div>
                  <div className="proactive-mode-content">
                    <span className="proactive-mode-label">{mode.label}</span>
                    <span className="proactive-mode-description">{mode.description}</span>
                  </div>
                  <div className="proactive-mode-check">
                    {proactiveMode === mode.value && <i className="bi bi-check-circle-fill"></i>}
                  </div>
                </div>
              ))}
            </div>
          </Form.Group>

          {/* Quiet Hours */}
          <Form.Group className="mb-4">
            <Form.Label className="proactive-label">
              <i className="bi bi-moon me-2"></i>
              Quiet Hours
            </Form.Label>
            <Form.Text className="text-muted d-block mb-2">
              I won't send proactive notifications during these times.
            </Form.Text>
            <Form.Select
              value={quietHoursPreset}
              onChange={(e) => handleQuietHoursPresetChange(e.target.value)}
              className="proactive-input mb-2"
            >
              {QUIET_HOURS_PRESETS.map((preset) => (
                <option key={preset.label} value={preset.label}>
                  {preset.label}
                </option>
              ))}
            </Form.Select>

            {quietHoursPreset === "Custom" && (
              <div className="d-flex gap-2">
                <div className="flex-grow-1">
                  <Form.Label className="small">Start</Form.Label>
                  <Form.Control
                    type="time"
                    value={quietHoursStart || ""}
                    onChange={(e) => setQuietHoursStart(e.target.value)}
                    className="proactive-input"
                  />
                </div>
                <div className="flex-grow-1">
                  <Form.Label className="small">End</Form.Label>
                  <Form.Control
                    type="time"
                    value={quietHoursEnd || ""}
                    onChange={(e) => setQuietHoursEnd(e.target.value)}
                    className="proactive-input"
                  />
                </div>
              </div>
            )}
          </Form.Group>

          {/* Confirmation Toggle */}
          <Form.Group className="mb-4">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <Form.Label className="proactive-label mb-0">
                  <i className="bi bi-shield-check me-2"></i>
                  Confirm External Actions
                </Form.Label>
                <Form.Text className="text-muted d-block">
                  Always ask before sending emails, creating calendar events, etc.
                </Form.Text>
              </div>
              <Form.Check
                type="switch"
                checked={alwaysConfirmExternal}
                onChange={(e) => setAlwaysConfirmExternal(e.target.checked)}
                className="proactive-switch"
              />
            </div>
          </Form.Group>

          {/* Heartbeat (Proactive Checks) */}
          <Form.Group className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <Form.Label className="proactive-label mb-0">
                  <i className="bi bi-heart-pulse me-2"></i>
                  Heartbeat (Proactive Checks)
                </Form.Label>
                <Form.Text className="text-muted d-block">
                  Periodic checks for upcoming events, overdue tasks, and pending reminders.
                </Form.Text>
              </div>
              <Form.Check
                type="switch"
                checked={heartbeatEnabled}
                onChange={(e) => setHeartbeatEnabled(e.target.checked)}
                className="proactive-switch"
              />
            </div>

            {heartbeatEnabled && (
              <>
                <Form.Label className="small fw-semibold">Check Mode</Form.Label>
                <div className="proactive-mode-options mb-3">
                  {[
                    { value: "lightweight", label: "Lightweight", icon: "bi-feather", desc: "Calendar + overdue tasks only" },
                    { value: "full", label: "Full", icon: "bi-clipboard-check", desc: "Calendar, tasks, reminders, inbox, idle check" },
                  ].map((m) => (
                    <div
                      key={m.value}
                      className={`proactive-mode-option ${heartbeatMode === m.value ? "active" : ""}`}
                      onClick={() => setHeartbeatMode(m.value)}
                    >
                      <div className="proactive-mode-icon">
                        <i className={`bi ${m.icon}`}></i>
                      </div>
                      <div className="proactive-mode-content">
                        <span className="proactive-mode-label">{m.label}</span>
                        <span className="proactive-mode-description">{m.desc}</span>
                      </div>
                      <div className="proactive-mode-check">
                        {heartbeatMode === m.value && <i className="bi bi-check-circle-fill"></i>}
                      </div>
                    </div>
                  ))}
                </div>

                <Form.Label className="small fw-semibold">Active Hours</Form.Label>
                <Form.Text className="text-muted d-block mb-2">
                  Heartbeat only runs during these hours (your timezone).
                </Form.Text>
                <div className="d-flex gap-2">
                  <div className="flex-grow-1">
                    <Form.Label className="small">Start</Form.Label>
                    <Form.Control
                      type="time"
                      value={heartbeatActiveStart}
                      onChange={(e) => setHeartbeatActiveStart(e.target.value)}
                      className="proactive-input"
                    />
                  </div>
                  <div className="flex-grow-1">
                    <Form.Label className="small">End</Form.Label>
                    <Form.Control
                      type="time"
                      value={heartbeatActiveEnd}
                      onChange={(e) => setHeartbeatActiveEnd(e.target.value)}
                      className="proactive-input"
                    />
                  </div>
                </div>
              </>
            )}
          </Form.Group>

          {/* Save Button */}
          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={saveSettings}
              disabled={saving}
              className="proactive-save-btn"
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-2"></i>
                  Save Preferences
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
